import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import Grid from "@material-ui/core/Grid";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SwipeableViews from "react-swipeable-views";
import { Stepper } from "@common/Stepper/Stepper";
import { EPAYMENT_INITIALISE_TYPES, EPAYMENT_TYPES, TITLE_PRIMARY_STYLE } from "@constants";
import { EJccFormTypes } from "../interface/IPayment";
import "./css/PaymentCardList.scss";
import { usePaymentCardList } from "./hook/usePaymentCardList";
import { PaymentCardAction } from "./PaymentCardAction";
import { PaymentCardItem } from "./PaymentCardItem";
import { HandleErrorAndLoading } from "@common/handleErrorAndLoading/HandleErrorAndLoading";
import CardPayment from "@features/Payment/ManageCards/CardPayment";
import { Modal } from "@common/Modal/Modal";

interface IParams {
    openedJccForm?: EJccFormTypes;
    setOpenedJccForm(openedJccForm?: EJccFormTypes): void;
}

export const PaymentCardList = (props: IParams) => {
    const { openedJccForm, setOpenedJccForm } = props;
    const {
        cardIsLoading,
        payerDetails,
        setDefaultCard,
        removeCard,
        cardError,
        retryCard,
        saveNewCard,
    } = usePaymentCardList(setOpenedJccForm);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [addCardSuccess, setAddCardSuccess] = useState<boolean>(false);
    const [arrowStep, setArrowStep] = useState(0);
    const { t } = useTranslation(["payment", "glossary"]);

    const handleStepChange = (step: number) => {
        setArrowStep(step);
    };

    const loadIframe = () => {
        setOpenedJccForm(EJccFormTypes.ADD_CARD);
    };

    const closeIframe = () => {
        setOpenedJccForm();
    };

    const finaliseAddCardCallback = (result: boolean) => {
        setAddCardSuccess(result);
        setIsModalOpen(true);
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        const getReceiptBinary = async () => {
            const orderId = searchParams.get("orderId");
            const from = searchParams.get("from");

            if (orderId && from && from === "addCard") {
                window.history.replaceState({}, document.title, window.location.href.split("?")[0]);
                saveNewCard(orderId, finaliseAddCardCallback);
            }
        };

        getReceiptBinary();
    }, []);

    const creditCards = payerDetails
        ? payerDetails.paymentMethods.filter(
              (paymentMethods) => paymentMethods.paymentMethodType === EPAYMENT_TYPES.CREDIT_CARD
          )
        : undefined;

    return (
        <>
            <Card elevation={2}>
                <CardHeader title={t("payment:manageCards")} titleTypographyProps={TITLE_PRIMARY_STYLE} />
                <HandleErrorAndLoading error={cardError} isLoading={cardIsLoading} retryFetch={retryCard}>
                    <CardContent className="payment-content">
                        {creditCards && creditCards.length > 0 && (
                            <Fragment>
                                <SwipeableViews
                                    enableMouseEvents
                                    index={arrowStep}
                                    slideStyle={{ display: "flex", justifyContent: "center", overflow: "hidden" }}
                                    style={{ display: "flex", justifyContent: "center" }}
                                    onChangeIndex={handleStepChange}
                                >
                                    {creditCards.map((card) => (
                                        <PaymentCardItem
                                            card={card}
                                            key={card.paymentMethodId}
                                            removeCard={removeCard}
                                            setDefaultCard={setDefaultCard}
                                        />
                                    ))}
                                </SwipeableViews>
                                <Grid alignItems="center" container justify="center">
                                    <Grid item xs={6}>
                                        <Stepper
                                            activeStep={arrowStep}
                                            fontSizeArrow="large"
                                            handleStepChange={handleStepChange}
                                            stepsLength={creditCards.length}
                                        />
                                    </Grid>
                                </Grid>
                            </Fragment>
                        )}
                        {openedJccForm === EJccFormTypes.ADD_CARD && (
                            <Grid className="payment-add" container justify="center">
                                <Grid item xs={12}>
                                    <CardPayment
                                        serviceId={0}
                                        isOnceOffPayment={false}
                                        loadDefaultCard={false}
                                        paymentType={EPAYMENT_INITIALISE_TYPES.ADD_CARD}
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </CardContent>
                    <PaymentCardAction
                        closeIframe={closeIframe}
                        isIframeDisplayed={openedJccForm === EJccFormTypes.ADD_CARD}
                        loadIframe={loadIframe}
                    />
                </HandleErrorAndLoading>
            </Card>
            <Modal
                close={() => setIsModalOpen(false)}
                confirm={() => setIsModalOpen(false)}
                confirmLabel={t("glossary:close")}
                content={addCardSuccess ? t("payment:addCardSuccessMessage") : t("payment:addCardFailedMessage")}
                isModalOpen={isModalOpen}
                title={addCardSuccess ? t("payment:addCardSuccessTitle") : t("payment:addCardFailedTitle")}
            />
        </>
    );
};
